import { ComponentType, useCallback, useEffect, useMemo, useState } from 'react';
import { FiActivity, FiArrowDownRight, FiArrowUpRight, FiClock, FiEye, FiGlobe, FiMonitor, FiRefreshCw, FiUsers } from 'react-icons/fi';
import { AnalyticsDashboardData, AnalyticsMetric, getAnalytics } from '../../../api/adminApi';
import './AnalyticsDashboard.css';

type IconProps = { 'aria-hidden'?: boolean | 'true' | 'false'; className?: string };
const icon = (value: unknown) => value as ComponentType<IconProps>;
const ActivityIcon = icon(FiActivity), DownIcon = icon(FiArrowDownRight), UpIcon = icon(FiArrowUpRight);
const ClockIcon = icon(FiClock), EyeIcon = icon(FiEye), GlobeIcon = icon(FiGlobe), MonitorIcon = icon(FiMonitor), RefreshIcon = icon(FiRefreshCw), UsersIcon = icon(FiUsers);

type Range = 7 | 30 | 90;
type Loader = (days: Range) => Promise<AnalyticsDashboardData>;

const number = (value = 0) => new Intl.NumberFormat('ko-KR').format(value);
const compact = (value = 0) => new Intl.NumberFormat('ko-KR', { notation: 'compact', maximumFractionDigits: 1 }).format(value);

const defaultLoader: Loader = async (days) => (await getAnalytics(days)).data;

const Trend = ({ value }: { value: number }) => {
    const positive = value >= 0;
    return (
        <span className={`analytics-trend ${positive ? 'positive' : 'negative'}`}>
            {positive ? <UpIcon /> : <DownIcon />}{Math.abs(value || 0)}%
        </span>
    );
};

const MetricCard = ({ icon, label, value, note, trend, accent }: { icon: React.ReactNode; label: string; value: string; note: string; trend?: number; accent: string }) => (
    <article className="analytics-metric-card" style={{ '--metric-accent': accent } as React.CSSProperties}>
        <div className="analytics-metric-top"><span className="analytics-metric-icon">{icon}</span>{trend !== undefined && <Trend value={trend} />}</div>
        <strong>{value}</strong>
        <span className="analytics-metric-label">{label}</span>
        <small>{note}</small>
    </article>
);

const LineChart = ({ data }: { data: AnalyticsDashboardData['daily'] }) => {
    const width = 760, height = 230, pad = 34;
    const max = Math.max(1, ...data.flatMap(item => [item.pageViews, item.visitors]));
    const points = (key: 'pageViews' | 'visitors') => data.map((item, index) => {
        const x = data.length === 1 ? width / 2 : pad + index * (width - pad * 2) / (data.length - 1);
        const y = height - pad - item[key] / max * (height - pad * 2);
        return `${x},${y}`;
    }).join(' ');

    return (
        <div className="analytics-line-wrap" aria-label="일별 접속 추이 그래프">
            {data.length === 0 ? <Empty compact /> : (
                <svg viewBox={`0 0 ${width} ${height}`} role="img">
                    {[0, 1, 2, 3, 4].map(i => <line key={i} x1={pad} x2={width - pad} y1={pad + i * 40} y2={pad + i * 40} className="chart-grid" />)}
                    <polyline points={points('pageViews')} className="chart-line pageviews" />
                    <polyline points={points('visitors')} className="chart-line visitors" />
                    {data.map((item, index) => {
                        const x = data.length === 1 ? width / 2 : pad + index * (width - pad * 2) / (data.length - 1);
                        const y = height - pad - item.pageViews / max * (height - pad * 2);
                        return <circle key={item.label} cx={x} cy={y} r="3.5" className="chart-dot"><title>{item.label}: {number(item.pageViews)}회</title></circle>;
                    })}
                </svg>
            )}
        </div>
    );
};

const HourlyChart = ({ data }: { data: AnalyticsDashboardData['hourly'] }) => {
    const map = new Map(data.map(item => [Number(item.hour), Number(item.value)]));
    const values = Array.from({ length: 24 }, (_, hour) => ({ hour, value: map.get(hour) || 0 }));
    const max = Math.max(1, ...values.map(item => item.value));
    return <div className="hourly-chart">{values.map(item => (
        <div className="hourly-column" key={item.hour} title={`${item.hour}시 · ${number(item.value)}회`}>
            <span className="hourly-value" style={{ height: `${Math.max(4, item.value / max * 100)}%` }} />
            {item.hour % 3 === 0 && <small>{item.hour}</small>}
        </div>
    ))}</div>;
};

const RankList = ({ items, emptyLabel = '아직 집계된 데이터가 없습니다.' }: { items: AnalyticsMetric[]; emptyLabel?: string }) => {
    const max = Math.max(1, ...items.map(item => Number(item.value)));
    if (!items.length) return <Empty text={emptyLabel} compact />;
    return <div className="analytics-rank-list">{items.map((item, index) => (
        <div className="analytics-rank" key={`${item.name}-${index}`}>
            <span className="rank-number">{String(index + 1).padStart(2, '0')}</span>
            <div className="rank-copy"><strong>{item.name || '알 수 없음'}</strong>{item.detail && <small>{item.detail}</small>}<span style={{ width: `${item.value / max * 100}%` }} /></div>
            <b>{number(item.value)}</b>
        </div>
    ))}</div>;
};

const Donut = ({ items }: { items: AnalyticsMetric[] }) => {
    const colors = ['#ef6a4c', '#1d8d83', '#e3a52f', '#6680b8', '#a46a9b'];
    const total = items.reduce((sum, item) => sum + Number(item.value), 0);
    let current = 0;
    const stops = items.map((item, index) => {
        const start = current; current += total ? item.value / total * 100 : 0;
        return `${colors[index % colors.length]} ${start}% ${current}%`;
    });
    return <div className="analytics-donut-block">
        <div className="analytics-donut" style={{ background: total ? `conic-gradient(${stops.join(',')})` : '#ece8df' }}><span><b>{compact(total)}</b><small>조회</small></span></div>
        <div className="analytics-legend">{items.slice(0, 5).map((item, index) => <div key={item.name}><i style={{ background: colors[index % colors.length] }} /><span>{item.name}</span><b>{total ? Math.round(item.value / total * 100) : 0}%</b></div>)}</div>
    </div>;
};

const Empty = ({ text = '방문 데이터가 쌓이면 이곳에 표시됩니다.', compact: small = false }: { text?: string; compact?: boolean }) => <div className={`analytics-empty ${small ? 'compact' : ''}`}><ActivityIcon /><span>{text}</span></div>;

const AnalyticsDashboard = ({ loadAnalytics = defaultLoader }: { loadAnalytics?: Loader }) => {
    const [days, setDays] = useState<Range>(30);
    const [data, setData] = useState<AnalyticsDashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try { setData(await loadAnalytics(days)); }
        catch { setError('접속 통계를 불러오지 못했습니다. 서버 연결과 관리자 로그인을 확인해 주세요.'); }
        finally { setLoading(false); }
    }, [days, loadAnalytics]);

    useEffect(() => { void load(); }, [load]);

    const peakHour = useMemo(() => data?.hourly.reduce((best, item) => item.value > best.value ? item : best, { hour: 0, value: 0 }), [data]);

    if (loading && !data) return <div className="analytics-dashboard"><div className="analytics-loading"><span /><span /><span /><span /></div></div>;
    if (error && !data) return <div className="analytics-dashboard"><div className="analytics-error"><GlobeIcon /><h2>통계를 연결할 수 없습니다</h2><p>{error}</p><button onClick={load}>다시 시도</button></div></div>;
    if (!data) return null;

    const s = data.summary;
    return <section className="analytics-dashboard">
        <header className="analytics-hero">
            <div><span className="analytics-eyebrow"><i /> LIVE INSIGHT</span><h1>접속 통계</h1><p>사람들이 어디서 들어와 무엇을 보고, 어떤 환경에서 머무는지 한눈에 확인하세요.</p></div>
            <div className="analytics-actions">
                <div className="analytics-range" aria-label="조회 기간">{([7, 30, 90] as Range[]).map(value => <button key={value} className={days === value ? 'active' : ''} onClick={() => setDays(value)}>{value}일</button>)}</div>
                <button className="analytics-refresh" onClick={load} disabled={loading}><RefreshIcon className={loading ? 'spinning' : ''} /> 새로고침</button>
            </div>
        </header>

        {error && <div className="analytics-inline-error">{error}</div>}

        <div className="analytics-metrics">
            <MetricCard icon={<EyeIcon />} label="페이지 조회" value={number(s.pageViews)} note="선택 기간 전체 조회 수" trend={data.comparison.pageViews} accent="#ef6a4c" />
            <MetricCard icon={<UsersIcon />} label="순 방문자" value={number(s.visitors)} note={`신규 ${number(s.newVisitors)} · 재방문 ${number(s.returningVisitors)}`} trend={data.comparison.visitors} accent="#1d8d83" />
            <MetricCard icon={<ActivityIcon />} label="세션" value={number(s.sessions)} note={`세션당 ${s.pagesPerSession || 0} 페이지`} trend={data.comparison.sessions} accent="#6680b8" />
            <MetricCard icon={<ClockIcon />} label="현재 방문자" value={number(s.liveVisitors)} note="최근 30분 순 방문자" accent="#e3a52f" />
        </div>

        <div className="analytics-grid analytics-grid-main">
            <article className="analytics-panel traffic-panel">
                <div className="panel-heading"><div><span>TRAFFIC OVERVIEW</span><h2>일별 접속 추이</h2></div><div className="chart-keys"><span><i className="pv" />조회</span><span><i className="uv" />방문자</span></div></div>
                <LineChart data={data.daily} />
                <div className="traffic-foot"><span>이탈률 <b>{s.bounceRate || 0}%</b></span><span>봇 제외 <b>{number(s.botViews)}건</b></span><span>마지막 갱신 <b>{new Date(data.generatedAt).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</b></span></div>
            </article>
            <article className="analytics-panel device-panel"><div className="panel-heading"><div><span>AUDIENCE</span><h2>기기 구성</h2></div><MonitorIcon /></div><Donut items={data.devices} /></article>
        </div>

        <div className="analytics-grid analytics-grid-three">
            <article className="analytics-panel"><div className="panel-heading"><div><span>CONTENT</span><h2>인기 페이지</h2></div></div><RankList items={data.topPages} /></article>
            <article className="analytics-panel"><div className="panel-heading"><div><span>ACQUISITION</span><h2>유입 경로</h2></div></div><RankList items={data.referrers} /></article>
            <article className="analytics-panel"><div className="panel-heading"><div><span>CAMPAIGN</span><h2>캠페인 성과</h2></div></div><RankList items={data.campaigns} /></article>
        </div>

        <div className="analytics-grid analytics-grid-main">
            <article className="analytics-panel"><div className="panel-heading"><div><span>ACTIVE HOURS</span><h2>시간대별 활동</h2></div><p>최다 {peakHour?.hour || 0}시 · {number(peakHour?.value || 0)}회</p></div><HourlyChart data={data.hourly} /></article>
            <article className="analytics-panel environment-panel"><div className="panel-heading"><div><span>TECHNOLOGY</span><h2>접속 환경</h2></div></div><div className="environment-tabs"><div><h3>브라우저</h3><RankList items={data.browsers.slice(0, 4)} /></div><div><h3>운영체제</h3><RankList items={data.operatingSystems.slice(0, 4)} /></div></div></article>
        </div>

        <div className="analytics-grid analytics-grid-two-small">
            <article className="analytics-panel"><div className="panel-heading"><div><span>LANGUAGE</span><h2>언어</h2></div></div><RankList items={data.languages} /></article>
            <article className="analytics-panel"><div className="panel-heading"><div><span>VIEWPORT</span><h2>화면 크기</h2></div></div><RankList items={data.screenSizes} /></article>
        </div>

        <article className="analytics-panel recent-panel">
            <div className="panel-heading"><div><span>RECENT ACTIVITY</span><h2>최근 방문</h2></div><p>익명 식별자는 앞 8자리만 표시됩니다.</p></div>
            {data.recentVisits.length ? <div className="recent-table-wrap"><table><thead><tr><th>시간</th><th>방문자</th><th>페이지</th><th>기기</th><th>브라우저 / OS</th><th>유입</th></tr></thead><tbody>{data.recentVisits.map(visit => <tr key={visit.id}><td>{new Date(visit.visitedAt).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td><td><code>{visit.visitor}</code></td><td><strong>{visit.path}</strong></td><td>{visit.device}</td><td>{visit.browser} · {visit.operatingSystem}</td><td>{visit.referrer}</td></tr>)}</tbody></table></div> : <Empty />}
        </article>
    </section>;
};

export default AnalyticsDashboard;
